<template>
  <div class="cpick__dim" @click.self="close">
    <div class="cpick" role="dialog" aria-label="전시 좌표 지정">
      <div class="cpick__head">
        <strong>좌표 지정</strong>
        <button type="button" class="cpick__close" @click="close" aria-label="닫기">×</button>
      </div>

      <div class="cpick__search">
        <input
            ref="queryInput"
            type="text"
            v-model="query"
            placeholder="장소명 또는 주소 (예: 국립현대미술관 서울)"
            @keyup.enter="search"
        />
        <button type="button" class="cpick__searchbtn" @click="search" :disabled="searching || !query.trim()">
          {{ searching ? '검색 중…' : '검색' }}
        </button>
      </div>

      <p v-if="searchError" class="cpick__error">{{ searchError }}</p>

      <div class="cpick__body">
        <div class="cpick__results">
          <p v-if="!searched" class="cpick__placeholder">장소명으로 검색하면 후보가 나옵니다.</p>
          <p v-else-if="!results.length" class="cpick__placeholder">
            검색 결과가 없습니다. 지점명을 빼거나 주소로 다시 찾아보세요.
          </p>
          <ul v-else>
            <li
                v-for="(place, idx) in results"
                :key="idx"
                :class="['cpick__result', {'is-selected': selectedIndex === idx}]"
                @click="selectPlace(place, idx)"
            >
              <div class="cpick__resulttop">
                <span class="cpick__name">{{ place.placeName }}</span>
                <span v-if="place.cultural" class="cpick__tag">문화시설</span>
                <span v-else-if="place.categoryGroupName" class="cpick__tag cpick__tag--muted">
                  {{ place.categoryGroupName }}
                </span>
              </div>
              <div class="cpick__addr">{{ place.roadAddressName || place.addressName }}</div>
              <div class="cpick__meta">
                <span v-if="place.phone">{{ place.phone }}</span>
                <a v-if="place.placeUrl" :href="place.placeUrl" target="_blank" rel="noopener noreferrer"
                   @click.stop>카카오맵에서 보기</a>
              </div>
            </li>
          </ul>
        </div>

        <div class="cpick__mapwrap">
          <div ref="mapEl" class="cpick__map"></div>
          <div v-if="mapError" class="cpick__mapfallback">
            <strong>지도를 불러오지 못했습니다.</strong>
            <p>{{ mapError }}</p>
            <p class="cpick__mapfallback-hint">
              지도가 없어도 <b>왼쪽 검색 결과를 선택하면 좌표가 지정됩니다.</b>
              지도를 쓰려면 카카오 개발자 콘솔의 플랫폼 &gt; Web 사이트 도메인에 <code>{{ origin }}</code> 을 등록해야 합니다.
            </p>
          </div>
          <p v-else class="cpick__maphint">지도를 클릭하거나 핀을 끌어서 위치를 미세 조정할 수 있습니다.</p>
        </div>
      </div>

      <div class="cpick__foot">
        <span class="cpick__picked">
          <template v-if="picked">선택한 좌표 — 위도 {{ picked.lat.toFixed(7) }} · 경도 {{ picked.lng.toFixed(7) }}</template>
          <template v-else>아직 좌표를 고르지 않았습니다.</template>
        </span>
        <span class="cpick__footbtns">
          <button type="button" @click="close">취소</button>
          <button type="button" class="cpick__confirm" :disabled="!picked" @click="confirm">이 좌표 사용</button>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import {nextTick, onBeforeUnmount, onMounted, ref} from 'vue';
import {apiClient} from '@/services/auth-header';

const props = defineProps({
  initialQuery: {type: String, default: ''},
  initialLat: {type: [String, Number], default: null},
  initialLng: {type: [String, Number], default: null},
});
const emit = defineEmits(['picked', 'close']);

const API_URL = '/contents';
const KAKAO_JS_KEY = process.env.VUE_APP_KAKAO_MAP_JAVASCRIPT_KEY || '';
const DEFAULT_CENTER = {lat: 37.5665, lng: 126.9780}; // 서울시청 — 아무것도 없을 때의 시작점

const query = ref(props.initialQuery || '');
const results = ref([]);
const searched = ref(false);
const searching = ref(false);
const searchError = ref('');
const selectedIndex = ref(-1);
const picked = ref(null);
const mapError = ref('');
const mapEl = ref(null);
const queryInput = ref(null);
const origin = window.location.origin;

let map = null;
let marker = null;

const close = () => emit('close');

const confirm = () => {
  if (!picked.value) {
    return;
  }
  emit('picked', {latitude: picked.value.lat, longitude: picked.value.lng});
};

const search = async () => {
  const q = query.value.trim();
  if (!q) {
    return;
  }
  searching.value = true;
  searchError.value = '';
  try {
    const {data} = await apiClient.get(API_URL + '/articles/exhibitions/locations/search', {params: {q}});
    results.value = data;
    searched.value = true;
    selectedIndex.value = -1;
  } catch (error) {
    console.error('장소 검색 실패:', error);
    // 서버가 이유를 문자열로 준다(키 미설정·인증 오류 등). 조용히 빈 목록을 보여주면 "검색이 안 된다" 로 오해된다
    searchError.value = typeof error?.response?.data === 'string'
        ? error.response.data
        : '장소 검색 중 오류가 발생했습니다.';
    results.value = [];
    searched.value = true;
  } finally {
    searching.value = false;
  }
};

const PLACE_ZOOM_LEVEL = 3; // 건물이 구분되는 배율 — 미세 조정을 하려면 이 정도까지는 들어가야 한다

const setPoint = (lat, lng, { center = true, zoom = null } = {}) => {
  picked.value = {lat, lng};
  if (!map || !window.kakao?.maps) {
    return;
  }
  const position = new window.kakao.maps.LatLng(lat, lng);
  if (marker) {
    marker.setPosition(position);
  }
  if (center) {
    map.setCenter(position);
  }
  // 검색 결과를 골랐을 때만 확대한다. 핀을 끄는 중에 배율이 바뀌면 손이 어긋난다.
  if (zoom !== null && map.getLevel() > zoom) {
    map.setLevel(zoom);
  }
};

const selectPlace = (place, idx) => {
  selectedIndex.value = idx;
  setPoint(Number(place.latitude), Number(place.longitude), {zoom: PLACE_ZOOM_LEVEL});
};

// SDK 가 이미 있으면 재사용, 없으면 한 번만 심는다. autoload=false 로 로드 시점을 우리가 잡는다.
const loadKakaoSdk = () => new Promise((resolve, reject) => {
  if (window.kakao?.maps?.LatLng) {
    resolve();
    return;
  }
  if (!KAKAO_JS_KEY) {
    reject(new Error('카카오 지도 JavaScript 키가 설정되지 않았습니다.'));
    return;
  }
  const existing = document.getElementById('kakao-maps-sdk');
  const onReady = () => window.kakao.maps.load(resolve);
  if (existing) {
    existing.addEventListener('load', onReady);
    existing.addEventListener('error', () => reject(new Error('카카오 지도 스크립트를 불러오지 못했습니다.')));
    return;
  }
  const script = document.createElement('script');
  script.id = 'kakao-maps-sdk';
  script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_JS_KEY}&autoload=false`;
  script.onload = onReady;
  // 도메인 미등록이면 카카오가 401 을 주고 여기로 떨어진다 — 화면에 그 사실과 등록할 오리진을 그대로 찍는다
  script.onerror = () => reject(new Error('카카오 지도 스크립트를 불러오지 못했습니다. (도메인 미등록 가능성)'));
  document.head.appendChild(script);
});

const initMap = async () => {
  try {
    await loadKakaoSdk();
    await nextTick();
    const start = picked.value || DEFAULT_CENTER;
    const center = new window.kakao.maps.LatLng(start.lat, start.lng);
    map = new window.kakao.maps.Map(mapEl.value, {center, level: picked.value ? 3 : 9});
    marker = new window.kakao.maps.Marker({position: center, draggable: true, map});

    window.kakao.maps.event.addListener(marker, 'dragend', () => {
      const p = marker.getPosition();
      setPoint(p.getLat(), p.getLng(), {center: false}); // 끌던 중에 지도가 튀면 손이 어긋난다
    });
    window.kakao.maps.event.addListener(map, 'click', (e) => {
      setPoint(e.latLng.getLat(), e.latLng.getLng(), {center: false});
    });
  } catch (error) {
    mapError.value = error.message;
  }
};

onMounted(async () => {
  const lat = Number(props.initialLat);
  const lng = Number(props.initialLng);
  if (Number.isFinite(lat) && Number.isFinite(lng) && props.initialLat !== null && props.initialLng !== null) {
    picked.value = {lat, lng};
  }
  await initMap();
  queryInput.value?.focus();
  if (query.value.trim()) {
    await search(); // 장소명이 이미 있으면 바로 후보를 보여준다 — 담당자가 두 번 입력하지 않게
  }
});

onBeforeUnmount(() => {
  map = null;
  marker = null;
});
</script>

<style scoped>
.cpick__dim {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cpick {
  width: min(1040px, 94vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.cpick__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #eee;
  font-size: 16px;
}

.cpick__close {
  border: 0;
  background: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: #666;
}

.cpick__search {
  display: flex;
  gap: 8px;
  padding: 14px 18px 8px;
}

.cpick__search input {
  flex: 1;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.cpick__searchbtn {
  padding: 0 18px;
  border: 0;
  border-radius: 4px;
  background: #1f2937;
  color: #fff;
  cursor: pointer;
}

.cpick__searchbtn:disabled {
  background: #c7ccd3;
  cursor: not-allowed;
}

.cpick__error {
  margin: 0 18px 4px;
  color: #c0392b;
  font-size: 13px;
}

.cpick__body {
  display: flex;
  gap: 14px;
  padding: 8px 18px 14px;
  min-height: 0;
  flex: 1;
}

.cpick__results {
  width: 380px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
}

.cpick__results ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.cpick__placeholder {
  padding: 18px;
  color: #999;
  font-size: 13px;
}

.cpick__result {
  padding: 10px 12px;
  border-bottom: 1px solid #f2f2f2;
  cursor: pointer;
}

.cpick__result:hover {
  background: #f7f9fb;
}

.cpick__result.is-selected {
  background: #eef6ff;
}

.cpick__resulttop {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cpick__name {
  font-weight: 600;
  font-size: 14px;
}

.cpick__tag {
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 9px;
  background: #e8f5e9;
  color: #2e7d32;
  white-space: nowrap;
}

.cpick__tag--muted {
  background: #f1f1f1;
  color: #888;
}

.cpick__addr {
  margin-top: 3px;
  font-size: 12px;
  color: #666;
}

.cpick__meta {
  margin-top: 3px;
  font-size: 12px;
  color: #999;
  display: flex;
  gap: 10px;
}

.cpick__mapwrap {
  flex: 1;
  position: relative;
  min-width: 0;
}

.cpick__map {
  width: 100%;
  height: 420px;
  border: 1px solid #eee;
  border-radius: 6px;
  background: #f4f4f4;
}

.cpick__maphint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #999;
}

.cpick__mapfallback {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 24px;
  background: #fffaf5;
  border: 1px solid #f0d8c0;
  border-radius: 6px;
  font-size: 13px;
  color: #8a5a2b;
}

.cpick__mapfallback-hint code {
  background: #f4e4d2;
  padding: 1px 5px;
  border-radius: 3px;
}

.cpick__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid #eee;
}

.cpick__picked {
  font-size: 13px;
  color: #555;
}

.cpick__footbtns {
  display: flex;
  gap: 8px;
}

.cpick__footbtns button {
  padding: 8px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

/*
 * 바로 위 .cpick__footbtns button 이 더 구체적이라, .cpick__confirm 만으로는 배경만 흰색으로 덮여
 * 흰 바탕에 흰 글씨가 된다(주 버튼이 통째로 안 보인다). 같은 경로를 붙여 특이도로 이긴다.
 */
.cpick__footbtns button.cpick__confirm {
  border-color: #1f2937;
  background: #1f2937;
  color: #fff;
}

.cpick__footbtns button.cpick__confirm:disabled {
  border-color: #e0e0e0;
  background: #e0e0e0;
  color: #999;
  cursor: not-allowed;
}
</style>
